import React from 'react';
import { Link } from 'react-router-dom';
import { Server, Brain, Cpu, ExternalLink } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';
import { TrustPortalScreenId } from '../../../data/trustPortalHelpText';

// Shared wrapper for the three live-vendor profile screens (Railway,
// Anthropic, OpenAI). Rather than duplicating the Vendor Management
// detail view, this screen acts as a friendly explainer page that links
// out to the live Vendor Management module — where the same detail view
// used by the TSG risk team lives. Help icons are wired into BOTH
// locations (here and inside VendorDetail.tsx) so reviewers can open
// context from either entry point.

interface VendorProfileLinkProps {
  vendor: 'railway' | 'anthropic' | 'openai';
}

const CONFIG: Record<
  VendorProfileLinkProps['vendor'],
  {
    name: string;
    tagline: string;
    role: string;
    tier: string;
    icon: React.ElementType;
    accentClass: string;
    helpScreenId: TrustPortalScreenId;
  }
> = {
  railway: {
    name: 'Railway',
    tagline: 'Production application hosting and infrastructure.',
    role: 'Railway runs the Jarvis backend and database workloads in production. All application code and tenant data lives inside Railway-managed environments.',
    tier: 'Tier 1 — critical infrastructure',
    icon: Server,
    accentClass: 'bg-orange-50 border-orange-200 text-orange-900',
    helpScreenId: 'vendor-profile-railway',
  },
  anthropic: {
    name: 'Anthropic',
    tagline: 'Primary large language model provider (Claude API).',
    role: 'Every AI-powered response generated by Jarvis ultimately calls the Anthropic Claude API. This makes Anthropic the highest-impact upstream model provider in the Jarvis stack.',
    tier: 'Tier 1 — primary AI provider',
    icon: Brain,
    accentClass: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    helpScreenId: 'vendor-profile-anthropic',
  },
  openai: {
    name: 'OpenAI',
    tagline: 'Secondary model provider for specific workloads.',
    role: 'OpenAI is used as a secondary model provider for specific features inside Jarvis. Refer to the Overview tab of the live vendor card for the current scope of OpenAI invocations.',
    tier: 'Tier 1 — AI provider',
    icon: Cpu,
    accentClass: 'bg-teal-50 border-teal-200 text-teal-900',
    helpScreenId: 'vendor-profile-openai',
  },
};

const VendorProfileLink: React.FC<VendorProfileLinkProps> = ({ vendor }) => {
  const config = CONFIG[vendor];
  const Icon = config.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gray-100 rounded-lg">
            <Icon className="w-6 h-6 text-gray-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Vendor Profile — {config.name}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{config.tagline}</p>
          </div>
        </div>
        <HelpIconButton screenId={config.helpScreenId} showLabel />
      </div>

      <div className={`p-5 rounded-xl border ${config.accentClass}`}>
        <div className="text-xs font-semibold uppercase tracking-wide">{config.tier}</div>
        <p className="mt-2 text-sm leading-relaxed">{config.role}</p>
      </div>

      <div className="p-5 bg-white border border-gray-200 rounded-xl">
        <h2 className="text-sm font-semibold text-gray-900">Live Vendor Management Card</h2>
        <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
          The canonical source of truth for {config.name} — including uploaded documents,
          review history, onboarding status, and key contacts — lives inside the Jarvis
          Vendor Management module. The help icon is wired in both places, so you can open
          context from either side.
        </p>
        <Link
          to="/app/vendor-management"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          Open Vendor Management
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        The vendor record is already seeded in Jarvis. Opening Vendor Management above
        will take you to the live detail view with the same data the TSG risk team sees.
      </div>
    </div>
  );
};

export default VendorProfileLink;
